import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { GET_ENTITY_TRANSLATIONS, TRANSLATE_TEXTS } from '../../data/definitions/entity-translation-definition';
import { BaseDataService } from '../../data/services/base-data.service';
import { EntityTranslationInput, EntityTranslationRow, GetEntityTranslationsQuery, GetEntityTranslationsQueryVariables, MutationTranslateTextsArgs, TranslateAiInput, TranslateAiResult } from '../../generated/generated-types';

/**
 * Reads translatable fields of any entity registered in the backend translation registry.
 * The registry is the only description of an entity the editor gets - omitting `fields`
 * returns everything registered, so there is no separate metadata query.
 * Writing goes through the entity's own save mutation, not through here.
 */
@Injectable({ providedIn: 'root' })
export class EntityTranslationService {

  private readonly baseDataService = inject(BaseDataService);

  async getEntityTranslations(input: EntityTranslationInput): Promise<EntityTranslationRow[]> {
    const result = await lastValueFrom(
      this.baseDataService.query<GetEntityTranslationsQuery, GetEntityTranslationsQueryVariables>(
        GET_ENTITY_TRANSLATIONS,
        { input },
        'network-only',
      ).single$,
    );
    return (result?.getEntityTranslations?.items ?? []) as EntityTranslationRow[];
  }

  async getRecordTranslations(entity: string, recordId: string, languages: EntityTranslationInput['languages']): Promise<EntityTranslationRow> {
    const rows = await this.getEntityTranslations({ entity, ids: [recordId], languages });
    return rows[0] ?? null;
  }

  /** Suggestions only - every requested key comes back, carrying either a text or an error. */
  async translateTexts(input: TranslateAiInput): Promise<TranslateAiResult> {
    const result = await lastValueFrom(
      this.baseDataService.mutate<{ translateTexts: TranslateAiResult }, MutationTranslateTextsArgs>(
        TRANSLATE_TEXTS,
        { input },
      ),
    );
    return result?.translateTexts ?? null;
  }
}
